import { User as FirebaseUser } from 'firebase/auth';
import { User } from 'interfaces';
import nookies from 'nookies';

import { currentUserEmitter } from 'modules/auth/store/authUserEmitter';
import { MeasurementKey } from 'modules/profiling/measurementTypes';
import { profilingStoreActions } from 'modules/profiling/profilingStore';
import { getCustomAccountSetting, refreshAccountSettings } from 'utils/baseAccountSettings';
import { clarityIdentifyUser } from 'utils/clarity';
import * as extension from 'utils/extension';
import { getSettings } from 'utils/extension';
import { bootIntercom } from 'utils/intercom';

import { useAuthStore } from '../index';
import { initSubscriptionAndEntitlements } from '../utils/subscription';
import { performAuthStoreCleanupFunctions } from './cleanupAuth';

const isUserChanged = (user: User | null | undefined, otherUser: User | null | undefined) => {
  if (!user?.metadata || !otherUser?.metadata) return true;

  return (
    user.email !== otherUser.email || //
    user.uid !== otherUser.uid ||
    user.isAnonymous !== otherUser.isAnonymous
  );
};

type Options = {
  force?: boolean;
};

let setUserInvocationsActive = 0;
let lastUser: User | null = null;
let lastInvocationId = 0;

export const setUser = (firebaseUser: FirebaseUser | null, { force = false }: Options = {}) => {
  if (!firebaseUser) {
    return;
  }

  let user = new User({ firebaseUser });

  // If we are not force refreshing than we check if setUser invocation with the same user is already in progress, if not then we check if we have the same user as the one that is set in the auth store
  if (!force) {
    if (setUserInvocationsActive > 0) {
      if (!isUserChanged(lastUser, user)) return;
    } else {
      if (!isUserChanged(useAuthStore.getState().user, user)) return;
    }
  }

  lastUser = user;

  lastInvocationId += 1;
  const invocationId = lastInvocationId;
  setUserInvocationsActive += 1;

  return profilingStoreActions
    .measureAction(
      async () => {
        useAuthStore.setState(state => ({
          user: state.user?.update({ firebaseUser })
        }));

        // We use the cache to speed up the first load, and then we can throw it away
        const cache = useAuthStore.getState().cache;
        useAuthStore.setState({ cache: undefined });

        // If we have cached properties we want to early emit some user object to speed up the first load, we will fetch new data in the background
        if (cache) {
          user = user.update({
            subscription: cache.subscription,
            entitlements: cache.entitlements,
            firstPdfDocument: cache.firstPdfDocument
          });

          useAuthStore.setState({
            isLoading: false,
            user
          });

          currentUserEmitter.emit('onUserUpdate', { user });
        }

        performAuthStoreCleanupFunctions();

        clarityIdentifyUser(user.uid);

        const [{ subscription, entitlements }, extensionSettings] = await Promise.all([initSubscriptionAndEntitlements(), getSettings()]);
        user = user.update({ subscription, entitlements });

        if (!extensionSettings?.voice?.name) {
          const landingPageVoiceName = nookies.get().lpvoice;
          if (landingPageVoiceName) {
            extension.setVoice({ name: landingPageVoiceName });
          }
        }

        if (!extensionSettings?.playbackSpeed) {
          const landingPageSpeed = Number(nookies.get().lppbspeed);
          if (landingPageSpeed) {
            extension.setPlaybackSpeed(landingPageSpeed);
          }
        }

        bootIntercom(user);

        await refreshAccountSettings();

        const firstPdfDocument = (await getCustomAccountSetting('firstPdfDocument')) ?? undefined;

        user = user.update({
          firstPdfDocument
        });

        // We don't want to touch the auth store from a stale invocation
        if (invocationId !== lastInvocationId) {
          return;
        }

        useAuthStore.setState({
          isLoading: false,
          user
        });

        currentUserEmitter.emit('onUserUpdate', { user });

        return user;
      },
      {
        measurementKey: MeasurementKey.setUser,
        identifier: {},
        context: {}
      }
    )
    .finally(() => {
      setUserInvocationsActive -= 1;
    });
};
